import { TableContainer } from '@chakra-ui/react';
import { AxiosResponse, HttpStatusCode } from 'axios';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAlertContext } from '../../../context/AlertProvider';
import { useApi } from '../../../context/ApiProvider';
import { useParentDashboardContextContext } from '../../../context/ParentDashboardContext';
import { ParentAbsenceInterfaceFormMoth } from '../../../shared/type/absence.type';
import CustomHorizontalTabs from '../../CustomHorizontalTabs/CustomHorizontalTabs';
import ChooseMonthsIn from '../../ChooseMonths/ChooseMonthsIn';

import './ParentAbsences.css';
import { useSafeTranslation } from '../../../hooks/useSafeTranslation';
import { ParentAbsencesTable } from './ParentAbsencesTable';

const ParentAbsences = () => {
  const today = new Date();
  const { t } = useSafeTranslation();
  const { setAlertProperties, setShow: setShowAlert } = useAlertContext();
  const { setBasketStatus } = useParentDashboardContextContext();
  const { apiAbsenceAndCourseController } = useApi();
  const { parentDashboardContext, fetchParentDashboardContext } =
    useParentDashboardContextContext();
  const [chosenTenantId, setChosenTenantId] = useState<number | null>(null);
  const [chosenConsumerId, setChosenConsumerId] = useState<number | null>(null);
  const [currentDate, setCurrentDate] = React.useState<{
    year: number;
    month: number;
    day: number;
  }>({
    year: today.getFullYear(),
    month: today.getMonth() + 1,
    day: today.getDate()
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [absences, setAbsences] = useState<ParentAbsenceInterfaceFormMoth[]>([]);

  const childrenToSelect = useMemo(() => {
    const parentDashboardData = parentDashboardContext
      ? parentDashboardContext.tenants.find((tenant) => tenant.tenantId === chosenTenantId)
      : null;

    const isParentHaveAnyChildren =
      parentDashboardData?.consumers && parentDashboardData?.consumers?.length > 0;

    if (isParentHaveAnyChildren) {
      return parentDashboardData.consumers.map((consumer) => {
        const { firstName, lastName, id } = consumer.consumerDto;
        return {
          value: id,
          label: firstName + ' ' + lastName
        };
      });
    }
    return null;
  }, [parentDashboardContext, chosenTenantId]);

  const reportOrRevokeAbsence = async (stakeId: number, presence: boolean) => {
    try {
      const response = await apiAbsenceAndCourseController('report-or-revoke-absence').post('', {
        consumerId: chosenConsumerId,
        boughtStakesId: [stakeId],
        absence: presence
      });

      if (response.status === HttpStatusCode.Ok) {
        setAlertProperties({
          description: t('parent_absences.update_success'),
          timeout: 9000,
          title: t('parent_absences.success'),
          status: 'success'
        });
        setShowAlert(true);

        setAbsences((prevAbsences) =>
          prevAbsences.map((absence) => ({
            ...absence,
            stakesAndCourses: absence.stakesAndCourses.map((stake) =>
              stake.boughtStakeId === stakeId ? { ...stake, presence: !stake.presence } : stake
            )
          }))
        );
        fetchParentDashboardContext();
      }
    } catch (error: any) {
      setAlertProperties({
        description: t('parent_absences.update_error', {
          action: presence ? 'zgłaszania' : 'odwoływania',
          error: error.response.data.errors[0]
        }),
        timeout: 9000,
        title: t('parent_absences.error'),
        status: 'error'
      });
      setShowAlert(true);
    }
  };

  const fetchAbsencesForCustomer = useCallback(async () => {
    setIsLoading(true);
    try {
      const response: AxiosResponse<ParentAbsenceInterfaceFormMoth[]> =
        await apiAbsenceAndCourseController('absences-for-consumer-for-month').post('', {
          consumerId: chosenConsumerId,
          tenantId: chosenTenantId,
          month: currentDate.month,
          year: currentDate.year
        });
      setAbsences(response.data);
    } catch (error) {
      setAlertProperties({
        description: t('parent_absences.fetch_error'),
        timeout: 9000,
        title: 'Błąd',
        status: 'error'
      });
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  }, [chosenConsumerId, chosenTenantId, currentDate]);

  useEffect(() => {
    setBasketStatus(() => ({ show: false, forceShow: false }));
  }, []);

  useEffect(() => {
    if (parentDashboardContext) {
      setChosenTenantId(parentDashboardContext.tenants[0].tenantId);
    }
  }, [parentDashboardContext]);

  useEffect(() => {
    if (chosenTenantId && parentDashboardContext) {
      const firstConsumerId =
        parentDashboardContext?.tenants.find((tenant) => tenant.tenantId === chosenTenantId)
          ?.consumers[0]?.consumerDto?.id || null;

      setChosenConsumerId(firstConsumerId);
    }
  }, [chosenTenantId]);

  useEffect(() => {
    if (chosenTenantId !== null && chosenConsumerId !== null) {
      const isChildFromSelectedSchool = parentDashboardContext?.tenants
        .find((tenant) => tenant.tenantId === chosenTenantId)
        ?.consumers.find((consumer) => consumer?.consumerDto?.id === chosenConsumerId);

      if (isChildFromSelectedSchool) fetchAbsencesForCustomer();
    }
  }, [chosenTenantId, chosenConsumerId, currentDate]);

  return (
    <section className="ParentAbsences flex flex-col bg-grayLight-50 overflow-y-auto">
      <div className="pt-spacing-3xl pr-spacing-3xl pb-spacing-xl pl-spacing-3xl">
        <div className="flex justify-between">
          <div className="flex flex-col">
            <h1 className="p-0 m-0 font-semibold text-grayLight-900" style={{ fontSize: '30px' }}>
              {t('parent_absences.title')}
            </h1>
            <p className="font-normal text-grayLight-600" style={{ fontSize: '16px' }}>
              {t('parent_absences.subtitle')}
            </p>
          </div>
          <div className="hidden lg:block">
            <ChooseMonthsIn setDate={setCurrentDate} currentDate={currentDate} mode="month" />
          </div>
        </div>
      </div>
      <div className="flex flex-col pt-spacing-xl pr-spacing-4xl pl-spacing-3xl pb-spacing-xl gap-spacing-xl">
        <CustomHorizontalTabs
          onChange={(value) => {
            setChosenTenantId(value);
          }}
          data={
            parentDashboardContext
              ? parentDashboardContext.tenants.map((tenant) => ({
                  label: tenant.tenantName,
                  value: tenant.tenantId
                }))
              : null
          }
        />

        <CustomHorizontalTabs
          onChange={(value) => {
            setChosenConsumerId(value);
          }}
          data={childrenToSelect}
        />
      </div>
      <div
        className="ParentAbsences__absenceTable flex flex-col pb-spacing-5xl"
        style={{ flexGrow: 1, overflowY: 'auto', marginBottom: '20px' }}>
        <TableContainer
          h="100%"
          flex="1"
          minH="500px"
          className="Branches__table bg-white rounded-lg pb-spacing-8xl">
          <TableContainer
            h="100%"
            flex="1"
            minH="500px"
            className="Branches__table bg-white rounded-lg pb-spacing-8xl">
            <ParentAbsencesTable
              absences={absences}
              isLoading={isLoading}
              onAbsenceToggle={reportOrRevokeAbsence}
            />
          </TableContainer>
        </TableContainer>
      </div>
    </section>
  );
};

export default ParentAbsences;
